import { ASTFragmentCursor } from "../../cursors/node_cursor";
import { ASTNodeDescriptor, GroupedNodeList } from "./descriptor";

export class ASTNode {
    /**
     * This is the interface through which external clients interact wtih an ASTNodeDescriptor.
     * To be able to support fragment reuse (for incremental parsing),
     * MovableASTNodes are created in a way such that their ranges are relative to 
     * the fragment within which they exist. This class fixes their position
     * to be absolute (a.k.a relative to the start of the source text)
     * 
     * This interface must be as "immutable" as possible to prevent any possible user 
     * modification of an instance from persisting
     */
    get name() { return this.node.name; }
    get classList() { return this.node.classList; }

    // TODO: Consider making this take in a nodeSet and a pointer to the node, and then internally it uses an ASTFragmentCursor
    constructor(
        /**
         * Node to be resolved 
         */
        private node: ASTNodeDescriptor,
        /**
         * A fragment cursor pointing to the provided anchoredNode
         */
        private fragmentCursor: ASTFragmentCursor
    ) {
        this.fragmentCursor = fragmentCursor.fork();
    }
    cursor() { this.fragmentCursor.fork(); }
    // caches
    private sourceNodesCache: GroupedNodeList|null = null;
    /**
     * returns the source syntax nodes from which the ASTNodeDescriptor that this class wraps was derived. 
     * The positions of all the source nodes are offset in order to be relative to the start of 
     * the source text, not relative to the start of the fragment it belongs to.
     * @returns a grouped list of source nodes from which this node was derived
     */
    sourceSyntaxNodes() { 
        if (this.sourceNodesCache) return this.sourceNodesCache;
        let sourceNodesCache: GroupedNodeList = {}
        Object.keys(this.node.sourceNodes).forEach((type) => {
            sourceNodesCache[type] = this.node.sourceNodes[type].map(node => {
                return node.createOffsetCopy(this.fragmentCursor.fragment.from);
            })
        })
        this.sourceNodesCache = sourceNodesCache;
        return this.sourceNodesCache;
    }

    firstChild() {
        const cursor = this.fragmentCursor.fork();
        if (!cursor.firstChild()) return null;
        return new ASTNode(cursor.node.node, cursor);
    }
    getChildren(): ASTNode[] {
        const cursor = this.fragmentCursor.fork();
        if (!cursor.firstChild()) return []
        const children: ASTNode[] = [];
        do {
            children.push(new ASTNode(cursor.node.node, cursor));
        } while (cursor.nextSibling());
        return children;
    }
    nextSibling() {
        const cursor = this.fragmentCursor.fork();
        if (!cursor.nextSibling()) return null;
        return new ASTNode(cursor.node.node, cursor);
    }
    prevSibling() {
        const cursor = this.fragmentCursor.fork();
        if (!cursor.prevSibling()) return null;
        return new ASTNode(cursor.node.node, cursor);
    }
    parent() {
        const cursor = this.fragmentCursor.fork();
        if (!cursor.parent()) return null;
        return new ASTNode(cursor.node.node, cursor);
    }

    getAncestors() {
        const cursor = this.fragmentCursor.fork();
        const ancestors:ASTNode[] = [];
        while (cursor.parent()) {
            ancestors.push(cursor.node);
        }
        return ancestors.reverse();
    }
}